import React, { useMemo, useRef, useState } from 'react';
import Select, { ValueType } from 'react-select';
import styles from './add-payment-receipt-modal.module.scss';
import Modal from '../../../../../shared/components/core-ui/modal/modal.component';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import Button, {
  ButtonSize,
  ButtonType
} from '../../../../../shared/components/core-ui/button/button.component';
import {
  InputText,
  InputTextErrorMode,
  InputTextType
} from '../../../../../shared/components/core-ui/input-text/input-text.component';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import ReactDatePicker from 'react-datepicker';
import useWindowResizer from '../../../../../shared/hooks/window-resizer/window-resizer.hook';
import { PaymentStatus } from '../../../../../shared/enums/payment-status.enum';
import 'react-dropzone-uploader/dist/styles.css';
import Dropzone from 'react-dropzone-uploader';
import { CURRENCY } from '../../../../../shared/enums/currency.enum';
import { UserRole } from '../../../../../shared/enums/user-role.enum';

export interface AddPaymentReceiptModalProps {
  config: {
    showModal: boolean;
    userRole?: UserRole;
    userRoles?: UserRole[];
  };
  data: {
    status?: PaymentStatus;
    paymentId?: string;
  };

  eventHandlers: {
    closeHandler: () => void;
    addPaymentReceiptHandler: (
      receipt: File,
      collectedAtDate: Date,
      status?: PaymentStatus,
      paymentId?: string,
      skipEmail?: boolean,
      bankCharges?: number,
      bankChargesCurrency?: string,
      forexDifference?: number
    ) => void;
  };
}

/**
 * functional component AddPaymentReceiptModal
 * @param {AddPaymentReceiptModalProps}
 */
type CurrencyOption = { value: string; label: string };

export const AddPaymentReceiptModal: React.FC<AddPaymentReceiptModalProps> = ({
  config,
  eventHandlers,
  data
}) => {
  const isPermited = (permittedList: UserRole[]) => {
    return (
      (config.userRole && permittedList.indexOf(config.userRole) > -1) ||
      permittedList.some((item) => config.userRoles?.includes(item))
    );
  };

  const [newReceipt, setNewReceipt] = useState<File>();
  const [skipEmail, setSkipEmail] = useState<boolean>(false);
  const [collectedAtDate, setCollectedAtDate] = useState<Date>(new Date());

  const inputIdPrefixRef = useRef(`payment-receipt-${Math.random().toString(36).slice(2)}`);
  const bankChargesId = `${inputIdPrefixRef.current}-bank-charges-amount`;
  const bankChargesCurrencyId = `${inputIdPrefixRef.current}-bank-charges-currency`;
  const forexDifferenceId = `${inputIdPrefixRef.current}-forex-differences`;

  const [bankCharges, setBankChargesAmount] = useState<number | undefined>(undefined);
  const [bankChargesCurrency, setBankChargesCurrency] = useState<string | undefined>(undefined);
  const [forexDifference, setForexDifferences] = useState<number | undefined>(undefined);

  const bankChargesCurrencyOptions: CurrencyOption[] = useMemo(
    () => [
      { value: 'egp', label: CURRENCY.egp },
      { value: 'usd', label: CURRENCY.usd }
    ],
    []
  );

  const selectedBankChargesCurrencyOption = useMemo(() => {
    if (!bankChargesCurrency) return null;
    return (
      bankChargesCurrencyOptions.find((option) => option.value === bankChargesCurrency) ?? null
    );
  }, [bankChargesCurrency, bankChargesCurrencyOptions]);

  const bankChargesCurrencyError = useMemo(() => {
    if (!bankCharges || bankCharges <= 0) return '';
    return bankChargesCurrency ? '' : 'Bank Charges Currency is required when amount is set.';
  }, [bankCharges, bankChargesCurrency]);

  const isMobileView = useWindowResizer();

  const handleNewPaymentReceiptSubmit = () => {
    if (newReceipt) {
      eventHandlers.addPaymentReceiptHandler(
        newReceipt,
        collectedAtDate,
        data.status,
        data.paymentId,
        skipEmail,
        bankCharges,
        bankChargesCurrency,
        forexDifference
      );
    }
  };

  const handleSkipEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSkipEmail(event.target.checked);
  };
  return (
    <Modal
      config={{ showModal: config.showModal, fullScreen: false, showClose: true }}
      eventHandlers={{
        closeModalHandler: () => {
          eventHandlers.closeHandler();
        }
      }}
    >
      <div className={styles['add-payment-receipt-modal']}>
        <div className={styles['add-payment-receipt-modal__title']}>Add Payment Receipt</div>
        <div className={styles['add-payment-receipt-modal__description']}>
          Collected At:
          <ReactDatePicker
            selected={collectedAtDate}
            onChange={(date) => setCollectedAtDate(date as Date)}
          />
        </div>

        {isPermited([
          UserRole.FINANCE_MANAGER,
          UserRole.FINANCE_SPECIALIST,
          UserRole.ADMIN,
          UserRole.SUPER_ADMIN
        ]) && (
          <div className={styles['add-payment-receipt-modal__description']}>
            <div className={styles['add-payment-receipt-modal__form-grid']}>
              <div className={styles['add-payment-receipt-modal__row']}>
                <div className={styles['add-payment-receipt-modal__field']}>
                  <label htmlFor={bankChargesId}>
                    Bank Charges Amount{' '}
                    <small className={styles['add-payment-receipt-modal__optional']}>
                      (optional)
                    </small>
                  </label>
                  <InputText
                    data={{
                      value: bankCharges ?? '',
                      placeholder: 'Enter amount'
                    }}
                    config={{
                      type: InputTextType.number,
                      showErrorMode: InputTextErrorMode.onFirstBlur
                    }}
                    eventHandlers={{
                      onChangeHandler: (value) => {
                        const parsedValue = parseFloat(value);
                        const nextAmount =
                          Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : undefined;
                        setBankChargesAmount(nextAmount);

                        if (!nextAmount) {
                          setBankChargesCurrency(undefined);
                        }
                      }
                    }}
                  />
                </div>

                <div className={styles['add-payment-receipt-modal__field']}>
                  <Select
                    classNamePrefix="react-select"
                    inputId={bankChargesCurrencyId}
                    value={selectedBankChargesCurrencyOption}
                    onChange={(option: ValueType<CurrencyOption, false>) => {
                      const selectedOption = option as CurrencyOption | null;
                      setBankChargesCurrency(selectedOption?.value ?? undefined);
                    }}
                    options={bankChargesCurrencyOptions}
                    isDisabled={!bankCharges || bankCharges <= 0}
                    placeholder="Select currency"
                  />
                </div>
              </div>

              <div className={styles['add-payment-receipt-modal__field']}>
                <label htmlFor={forexDifferenceId}>
                  Forex Differences{' '}
                  <small className={styles['add-payment-receipt-modal__optional']}>
                    (optional)
                  </small>
                </label>
                <InputText
                  data={{
                    value: forexDifference ?? '',
                    placeholder: 'Enter amount'
                  }}
                  config={{
                    type: InputTextType.number,
                    showErrorMode: InputTextErrorMode.onFirstBlur
                  }}
                  eventHandlers={{
                    onChangeHandler: (value) => {
                      const parsedValue = parseFloat(value);
                      setForexDifferences(
                        Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : undefined
                      );
                    }
                  }}
                />
              </div>
            </div>
          </div>
        )}
        <Dropzone
          classNames={{ input: styles['add-payment-receipt-modal__dropzone'] }}
          inputContent={() => (
            <div className={styles['add-payment-receipt-modal__upload-container']}>
              <CloudUploadIcon fontSize={'large'} />
              Upload proof of payment
            </div>
          )}
          accept="*"
          maxFiles={1}
          multiple={false}
          onChangeStatus={(file) => {
            setNewReceipt(file.file);
          }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={skipEmail}
              onChange={handleSkipEmailChange}
              inputProps={{ 'aria-label': 'primary checkbox' }}
            />
          }
          label="Do NOT send email to guest"
        />
        <div className={styles['add-payment-receipt-modal--actions']}>
          <Button
            onClick={() => {
              eventHandlers.closeHandler();
            }}
            type={ButtonType.primaryInverted}
            tracking={{
              action: 'close',
              category: 'payments',
              label: 'add-payment-modal'
            }}
            size={isMobileView ? ButtonSize.full : ButtonSize.medium}
          >
            Cancel
          </Button>
          <Button
            disabled={!newReceipt || !!bankChargesCurrencyError}
            onClick={handleNewPaymentReceiptSubmit}
            tracking={{
              action: 'submit',
              category: 'payment',
              label: 'add-payment-receipt-modal'
            }}
            size={isMobileView ? ButtonSize.full : ButtonSize.medium}
          >
            Add payment
          </Button>
        </div>
      </div>
    </Modal>
  );
};
